import sessionApi from "../../api/session";
import * as mutationTypes from "../mutation-types";
// Issue 2188: router は packs/application.js からも静的インポートされており、
// 常にエントリーチャンクに含まれる。そのため本ファイルで動的インポートしても
// チャンク分離の効果はなく、Rollup の静的/動的インポート混在警告の原因になっていた。
// 効果のない動的インポートをやめ、静的インポートに統一する。
import router from "../../routes/index";
import { normalizeMaintenanceNoticeBody } from "@/vue/entities/maintenance-notice";
import { createLatestRequestGuard } from "../../utils/latestRequestGuard";

// ログイン画面の再マウントごとに並行して取得され得るため、最後に発行したリクエストの応答だけを反映する。
// 先に発行した古い応答が後から届いても、終了済みのお知らせや古い emailLoginEnabled で上書きしない（REQ-109）
const guardFetchAuthSetting = createLatestRequestGuard({ mode: "issued" });

function initialState() {
  return {
    currentDoctor: null,
    loggedIn: false,
    emailLoginEnabled: false,
    maintenanceNoticeBody: null
  };
}

const state = initialState();

const getters = {
  loggedIn: state => state.loggedIn,
  loginAction: state => state.loginAction,
  currentDoctor: state => state.currentDoctor,
  capabilities: state => {
    const doctor = state.currentDoctor;
    if (!doctor) return [];

    return Array.isArray(doctor.capabilities) ? doctor.capabilities : [];
  },
  hasAllCapabilities: (state, getters) => (required = []) => {
    if (!required || required.length === 0) return true;

    const capabilities = getters.capabilities || [];
    return required.every(capability => capabilities.includes(capability));
  },
  // 未取得・未定義のフラグは無効（新運用）として扱う
  isFeatureEnabled: state => key => state.currentDoctor?.featureFlags?.[key] === true,
  doctorName: state => state.currentDoctor?.name || "",
  doctorProfileUrl: state => {
    const doctor = state.currentDoctor;
    if (!doctor?.obstetricsHost || !doctor?.id) return "";
    return `${doctor.obstetricsHost}/doctors/${doctor.id}`;
  },
  emailLoginEnabled: state => state.emailLoginEnabled,
  maintenanceNoticeBody: state => state.maintenanceNoticeBody
};


const actions = {
  login({ commit, dispatch }, params) {
    return new Promise((resolve) => {
      sessionApi.login((result) => {
        const { loggedIn, error } = result;
        let loginAction = true;
        commit(mutationTypes.ASSIGN_LOGGED_IN, { loggedIn });
        if( loggedIn == true ){
          dispatch("fetchCurrentDoctor").then(()=> {
            if(params.redirectPath){
              router.push(params.redirectPath);
            } else {
              // 権限によりログイン先が変わる
              router.push(state.currentDoctor.homeUrl);
            }
          });
        }
        resolve({ loggedIn, error });
      }, params);
    });
  },

  logout({ commit, dispatch }) {
    return new Promise((resolve) => {
      sessionApi.logout(loggedIn => {
        commit(mutationTypes.ASSIGN_LOGGED_IN, { loggedIn });
        let currentDoctor = null;
        commit(mutationTypes.ASSIGN_CURRENT_DOCTOR, { currentDoctor });
        // ページを再読み込みせずにログイン画面へ戻るため、次にログインした相談員へ
        // ログアウト前の相談一覧が表示されないよう消しておく
        dispatch("resetAnytimeConsultationsList");
        router.push("/login").catch(() => {}).finally(() => resolve());
      });
    });
  },

  checkLoggedIn({ commit, dispatch }) {
    return new Promise((resolve) => {
      sessionApi.checkLoggedIn(loggedIn => {
        commit(mutationTypes.ASSIGN_LOGGED_IN, { loggedIn });
        if(loggedIn){
          dispatch("fetchCurrentDoctor");
        }
        resolve();
      });
    });
  },

  fetchCurrentDoctor({ commit }) {
    return new Promise((resolve) => {
      sessionApi.fetchCurrentDoctor(currentDoctor => {
        commit(mutationTypes.ASSIGN_CURRENT_DOCTOR, { currentDoctor });
        if (currentDoctor.id > 0) {
          let loggedIn = true;
          commit(mutationTypes.ASSIGN_LOGGED_IN, { loggedIn });
        }
        resolve();
      });
    });
  },

  fetchAuthSetting({ commit }) {
    // SPA内でログアウトして戻った場合に、前回の本文が残らないよう先にクリアする（REQ-108, REQ-109）
    commit(mutationTypes.ASSIGN_MAINTENANCE_NOTICE_BODY, { maintenanceNoticeBody: null });
    const applyIfLatest = guardFetchAuthSetting().guard(setting => {
      let emailLoginEnabled = setting.email_login_enabled;
      commit(mutationTypes.EMAIL_LOGIN_ENABLED, { emailLoginEnabled });
      commit(mutationTypes.ASSIGN_MAINTENANCE_NOTICE_BODY, {
        maintenanceNoticeBody: normalizeMaintenanceNoticeBody(setting.maintenance_notice_body)
      });
    });
    return new Promise((resolve) => {
      sessionApi.fetchAuthSetting(setting => {
        applyIfLatest(setting);
        resolve();
      }, () => {
        // 取得に失敗してもログイン画面の操作を妨げないよう、reject せずに resolve する。
        // emailLoginEnabled は既存の挙動どおり前の値のまま変更しない（REQ-108, REQ-403）
        resolve();
      });
    });
  },
};


const mutations = {
  [mutationTypes.ASSIGN_LOGIN_ACTION](state, { loginAction }) {
    state.loginAction = loginAction;
  },

  [mutationTypes.ASSIGN_LOGGED_IN](state, { loggedIn }) {
    state.loggedIn = loggedIn;
  },

  [mutationTypes.ASSIGN_CURRENT_DOCTOR](state, { currentDoctor }) {
    state.currentDoctor = currentDoctor;
  },

  [mutationTypes.EMAIL_LOGIN_ENABLED](state, { emailLoginEnabled }) {
    state.emailLoginEnabled = emailLoginEnabled;
  },

  [mutationTypes.ASSIGN_MAINTENANCE_NOTICE_BODY](state, { maintenanceNoticeBody }) {
    state.maintenanceNoticeBody = maintenanceNoticeBody;
  },

  // state はプレーンオブジェクトで、createStore のたびに同じオブジェクトが使い回される。
  // テスト間で state が漏れないよう、初期状態に戻すために使う
  [mutationTypes.RESET_SESSIONS_STATE](state) {
    const newState = initialState();
    Object.keys(newState).forEach(key => {
      state[key] = newState[key];
    });
  }
};


export default {
  state,
  getters,
  actions,
  mutations
};
